import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  Sheet,
  SheetDescription,
} from "@/components/ui/sheet";
import { Reservation } from "@/interface/entityInterface";
import { format } from "date-fns";
import { Car, Building2, MapPin, Phone, User, Mail, Calendar, StickyNote, Wrench } from "lucide-react";
import { Dispatch, FC, SetStateAction } from "react";

interface sheet {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  row: Reservation | null;
}

const ReservationDetail: FC<sheet> = ({ open, setOpen, row }) => {

  if (!row) return;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="max-h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Reservation details</SheetTitle>
          <SheetDescription>Full details</SheetDescription>
        </SheetHeader>
         <div className="p-4">
      <Card className="w-full p-6">
        <CardContent className="space-y-6">

          {/* HEADER */}
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 text-2xl font-bold">
              <Car className="w-6 h-6 text-orange-500" />
              Garage Management System
            </div>
            <div className="text-sm">
              Reservation ID: #{row?.id}
            </div>
          </div>

          {/* MAIN DETAILS */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-lg">

            {/* Branch */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-semibold">
                <Building2 className="w-4 h-4" /> {row?.branches?.name}
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" /> {row?.branches?.address}
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> {row?.branches?.phone}
              </div>
            </div>

            {/* Client */}
            <div className="space-y-1">
              <div className="flex items-center gap-2 font-semibold">
                <User className="w-4 h-4" /> {row?.guest_user?.name}
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" /> {row?.guest_user?.phone}
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" /> {row?.guest_user?.email}
              </div>
            </div>

            {/* Reservation Info */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> {row?.date && format(row.date, "dd-MMMM-yyyy HH:mm")}
              </div>
              <div className="flex items-center gap-2">
                Status: {
                  row?.status === "PENDING" ? (
                    <Badge className="bg-yellow-100 text-yellow-600">Pending</Badge>
                  ) : row?.status === "IN PROGRESS" ? (
                    <Badge className="bg-orange-100 text-orange-600">In progress</Badge>
                  ) : (
                    <Badge className="bg-green-100 text-green-600">Completed</Badge>
                  )
                }
              </div>
              {row?.notes && (
                <div className="flex items-center gap-2">
                  <StickyNote className="w-4 h-4" /> {row?.notes}
                </div>
              )}
              <div className="flex items-center gap-2">
                <Wrench className="w-4 h-4" /> {row?.technician?.firstname} {row?.technician?.lastname}
              </div>
            </div>
          </div>

          {/* VEHICLE INFO */}
          <div className="overflow-auto">
            <table className="w-full text-sm border rounded">
              <thead className="bg-muted">
                <tr>
                  <th className="p-2 text-left">VIN</th>
                  <th className="p-2 text-left">Make</th>
                  <th className="p-2 text-left">Model</th>
                  <th className="p-2 text-left">Year</th>
                  <th className="p-2 text-left">Mileage</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b">
                  <td className="p-2">{row?.guest_vehicle?.vin}</td>
                  <td className="p-2">{row?.guest_vehicle?.make}</td>
                  <td className="p-2">{row?.guest_vehicle?.model}</td>
                  <td className="p-2">{row?.guest_vehicle?.license_plate}</td>
                  <td className="p-2">{row?.guest_vehicle?.year}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* SERVICES */}
          <div className="overflow-auto">
            <table className="w-full text-sm border rounded">
              <thead className="bg-muted">
                <tr>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Quantity</th>
                  <th className="p-2 text-left">Price</th>
                </tr>
              </thead>
              <tbody>
                {row?.reservation_items?.map((item) => (
                  <tr className="border-b" key={item?.id}>
                    <td className="p-2">{item.inventory?.name} </td>
                    <td className="p-2">{item?.quantity} {item.inventory?.unit}</td>
                    <td className="p-2">${item?.price}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
      </SheetContent>
    </Sheet>
  );
};

export default ReservationDetail;
